import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { analysesApi, spectrumApi } from "@/services/api";
import type { ChromatogramPoint, MassSpectrumData, MzRtPoint } from "@/types";
import type {
  PreprocessingKey,
  SpectrumComparisonData,
  SpectrumHitsData,
} from "@/types/spectrumComparison";

export function useSpectrumTIC(sampleId: number | undefined) {
  return useQuery({
    queryKey: ["spectrum", "tic", sampleId],
    queryFn: async () => {
      if (!sampleId) return [];
      const response = await spectrumApi.getTICData(sampleId);
      return response.data.tic_data as ChromatogramPoint[];
    },
    enabled: !!sampleId,
  });
}

export function useSpectrumMZRT(
  sampleId: number | undefined,
  analysisId?: number,
  maxPoints?: number,
) {
  return useQuery({
    queryKey: ["spectrum", "mzrt", sampleId, analysisId, maxPoints],
    queryFn: async () => {
      if (!sampleId) return [];
      const response = await spectrumApi.getMZRTData(
        sampleId,
        analysisId,
        maxPoints,
      );
      return response.data.ms_data as MzRtPoint[];
    },
    enabled: !!sampleId,
  });
}

export function useMassSpectrum(sampleId: number | undefined, rt?: number) {
  return useQuery({
    queryKey: ["spectrum", "mass", sampleId, rt],
    queryFn: async () => {
      if (!sampleId) return [];
      const response = await spectrumApi.getMassSpectrum(sampleId, rt);
      return response.data.spectrum as MassSpectrumData[];
    },
    enabled: !!sampleId && rt !== undefined,
    placeholderData: keepPreviousData,
  });
}

/**
 * 검출 물질 하나의 실험↔참조 스펙트럼 비교 (S2).
 *
 * `resultId` 는 백엔드 `AnalysisResult.id` 다. mockup fallback 데이터에는
 * 없으므로 그 경우 요청 자체를 보내지 않는다 — 없는 근거로 비교 화면을
 * 열지 않기 위해서다.
 */
export function useSpectrumComparison(
  analysisId: number | undefined,
  resultId: number | undefined,
  compoundId?: number,
  mzTolerance?: number,
) {
  return useQuery({
    queryKey: [
      "analysis",
      "spectrum-comparison",
      analysisId,
      resultId,
      compoundId,
      mzTolerance,
    ],
    queryFn: async () => {
      if (!analysisId || !resultId) return null;
      const response = await analysesApi.getSpectrumComparison(
        analysisId,
        resultId,
        compoundId,
        mzTolerance,
      );
      return response.data as SpectrumComparisonData;
    },
    enabled: !!analysisId && !!resultId,
    placeholderData: keepPreviousData,
  });
}

/**
 * 참조 라이브러리 전체 순위 (S7 — 원본의 Hit List).
 *
 * 실험 스펙트럼 하나를 참조 125종과 견준다. **AI 후보가 참조 DB 에
 * 없어도 순위는 나온다** — 비교 화면이 비는 경우(프로덕션 검출의 절반)에도
 * 이 목록은 쓸모가 있다.
 */
/**
 * 스캔(RT) 앵커 순위 — 후보 선택이 없을 때 선택 RT 의 스캔으로 참조 전량
 * 순위를 열람한다 (검출 표시 개선 1단계). 저장 행이 하나도 없는 스캔(후보
 * 전원 기준 미달 등)도 열린다 — 이전에는 동정 대상 스캔의 61% 가 열람
 * 불가였다.
 */
export function useSpectrumHitsByScan(
  analysisId: number | undefined,
  scanRt: number | undefined,
  preprocessing?: PreprocessingKey,
) {
  return useQuery({
    queryKey: [
      "analysis",
      "spectrum-hits-by-scan",
      analysisId,
      scanRt,
      preprocessing,
    ],
    queryFn: async () => {
      if (!analysisId || scanRt == null) return null;
      const response = await analysesApi.getSpectrumHitsByScanRt(
        analysisId,
        scanRt,
        preprocessing,
      );
      return response.data as SpectrumHitsData;
    },
    enabled: !!analysisId && scanRt != null,
    placeholderData: keepPreviousData,
  });
}

export function useSpectrumHits(
  analysisId: number | undefined,
  resultId: number | undefined,
  preprocessing?: PreprocessingKey,
) {
  return useQuery({
    queryKey: [
      "analysis",
      "spectrum-hits",
      analysisId,
      resultId,
      preprocessing,
    ],
    queryFn: async () => {
      if (!analysisId || !resultId) return null;
      const response = await analysesApi.getSpectrumHits(
        analysisId,
        resultId,
        preprocessing,
      );
      return response.data as SpectrumHitsData;
    },
    enabled: !!analysisId && !!resultId,
    placeholderData: keepPreviousData,
  });
}
